const { isAppForeground } = require('../utils/app-visibility');

const ONE_SEC_MS = 1000;
const SYNC_REQUEST_LEASE_MS = 30 * ONE_SEC_MS;

let notificationPoll = null;
let notificationPollTimeout = null;

const internals = {};

internals.pollingEnabled = false;
internals.nativePaused = false;
internals.requestLease = null;

internals.canPoll = () => {

    const { context } = require('../app-context');
    const selectors = context.selectors?.all;
    const state = internals.store?.getState() || context.redux?.hooks?.getState?.();

    return !internals.nativePaused && isAppForeground({ selectors, state })
        && (!state || !selectors?.getIsAuthenticated || selectors.getIsAuthenticated(state))
        && (!state || !selectors?.getVerifiedStatus || selectors.getVerifiedStatus(state));
};

internals.clearPollingTimers = () => {

    clearTimeout(notificationPollTimeout);
    clearInterval(notificationPoll);
    notificationPollTimeout = null;
    notificationPoll = null;
};

internals.schoolAssetFields = [
    'logoVersion',
    'tileLogoVersion',
    'headerLogoPath',
    'headerLogoVersion'
];

exports.startPollingNotifs = (dispatch) => {

    const { context } = require('../app-context');
    const actions = context.actions;
    internals.pollingEnabled = true;

    if (!internals.canPoll() || internals.isPollingNotifs()) {
        return;
    }

    const poll = () => {

        if (!internals.pollingEnabled || !internals.canPoll()) {
            internals.clearPollingTimers();
            return;
        }

        if (internals.requestLease && Date.now() < internals.requestLease.expiresAt) {
            return;
        }

        // A stalled HTTP request must not suppress polling indefinitely. Completion
        // only releases its own lease, including after a session or store change.
        const lease = { expiresAt: Date.now() + SYNC_REQUEST_LEASE_MS };
        internals.requestLease = lease;
        const complete = () => {

            if (internals.requestLease === lease) {
                internals.requestLease = null;
            }
        };

        try {
            const result = dispatch(actions.dataFetching.fetchSync());

            if (result && typeof result.then === 'function') {
                result.then(complete, complete);
            }
            else {
                complete();
            }
        }
        catch (error) {
            complete();
            throw error;
        }
    };

    // Schedule to happen almost immediately– don't want to dispatch unconditionally in time with subscribe()
    notificationPollTimeout = setTimeout(() => {
        notificationPollTimeout = null;
        poll();
    });

    notificationPoll = setInterval(
        poll,
        8 * ONE_SEC_MS
    );
};

exports.stopPollingNotifs = () => {

    internals.pollingEnabled = false;
    internals.requestLease = null;
    internals.clearPollingTimers();

    return null;
};

internals.isPollingNotifs = () => !!(notificationPoll || notificationPollTimeout);

internals.installSchoolAssetSync = (store, context) => {

    const selectors = context.selectors.all;
    const http = context.http.nearpeer;

    http.interceptors.request.use((config) => {

        if (config.url === '/notifications' || config.url === '/sync') {
            config.schoolId = selectors.getCurrentSchoolId(store.getState());
        }

        return config;
    });

    http.interceptors.response.use((response) => {

        const schoolAssetsResponse = response.config.url === '/sync'
            ? response.data?.notifications
            : response.data;

        if (!['/notifications', '/sync'].includes(response.config.url) || !schoolAssetsResponse?.schoolAssets) {
            return response;
        }

        const state = store.getState();
        const schoolId = selectors.getCurrentSchoolId(state);
        const school = selectors.getCurrentSchool(state);

        if (!school || schoolId !== response.config.schoolId) {
            return response;
        }

        const schoolAssets = internals.schoolAssetFields.reduce((assets, field) => ({
            ...assets,
            [field]: schoolAssetsResponse.schoolAssets[field]
        }), {});

        if (internals.schoolAssetFields.some((field) => school[field] !== schoolAssets[field])) {
            store.dispatch({
                type: 'DATA_FETCHING/SCHOOL_ASSETS/SUCCESS',
                payload: {
                    result: {
                        entities: {
                            schools: {
                                [schoolId]: {
                                    id: schoolId,
                                    ...schoolAssets
                                }
                            }
                        }
                    }
                }
            });
        }

        return response;
    });
};

exports.initialize = (store) => {

    if (internals.store === store) {
        return internals.cleanup;
    }

    internals.cleanup?.();
    internals.store = store;
    const { context } = require('../app-context');

    const selectors = context.selectors.all;

    internals.installSchoolAssetSync(store, context);

    let wasEligible = false;

    const updatePolling = () => {

        const state = store.getState();
        const isAuthenticated = selectors.getIsAuthenticated(state);
        const isVerified = selectors.getVerifiedStatus(state);

        const eligible = isAuthenticated && isVerified;

        if (eligible && !wasEligible) {
            internals.pollingEnabled = true;
        }

        wasEligible = eligible;

        if (!eligible) {
            exports.stopPollingNotifs();
        }
        else if (!internals.canPoll()) {
            internals.clearPollingTimers();
        }
        else if (internals.pollingEnabled) {
            exports.startPollingNotifs(store.dispatch);
        }
    };

    const onPause = () => {

        internals.nativePaused = true;
        updatePolling();
    };
    const onResume = () => {

        internals.nativePaused = false;
        updatePolling();
    };
    const unsubscribe = store.subscribe(updatePolling);

    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', updatePolling);
        document.addEventListener('pause', onPause);
        document.addEventListener('resume', onResume);
    }

    internals.cleanup = () => {

        exports.stopPollingNotifs();
        unsubscribe?.();

        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', updatePolling);
            document.removeEventListener('pause', onPause);
            document.removeEventListener('resume', onResume);
        }

        internals.store = null;
        internals.cleanup = null;
        internals.nativePaused = false;
    };

    updatePolling();

    return internals.cleanup;
};
